/**
 * SC-375: grid geometry for the Gallery display mode. Pure, so it can be tested without a DOM.
 *
 * Every tile gets the same 16:9 box. The column count is whichever makes that box biggest inside the
 * available area, which gives the familiar meeting grid: one tile fills the screen, two sit side by
 * side (stacked on a portrait screen), four make a 2×2, and so on.
 *
 * How many tiles fit depends on the screen: `capacity` is the most tiles that can be shown while each
 * stays at least `minTileWidth` wide and at least `minTileWidthFraction` of the area's width. The
 * fraction makes a big display show bigger tiles rather than ever more of them, levelling off at about
 * a 7×7 grid; small screens are governed by the pixel minimum alone. Anyone beyond `capacity` is folded
 * into a "+N others" tile by the caller.
 */

export const GALLERY_TILE_ASPECT = 16 / 9;
export const GALLERY_MIN_TILE_WIDTH = 240;
export const GALLERY_MIN_TILE_WIDTH_FRACTION = 1 / 8;
export const GALLERY_TILE_GAP = 12;
/**
 * Ceiling regardless of screen size. Every visible tile is a video the browser decodes, so this bounds
 * the cost; it matches the 7×7 grid the width fraction levels off at.
 */
export const GALLERY_MAX_TILES = 49;

export interface GalleryLayoutInput {
  count: number;
  width: number;
  height: number;
  gap?: number;
  aspect?: number;
  minTileWidth?: number;
  minTileWidthFraction?: number;
  maxTiles?: number;
}

export interface GalleryLayout {
  cols: number;
  rows: number;
  tileWidth: number;
  tileHeight: number;
  /** Most tiles this area can show at the minimum tile width or wider (at least 1). */
  capacity: number;
}

interface Fit {
  cols: number;
  rows: number;
  tileWidth: number;
  tileHeight: number;
}

function fitFor(count: number, cols: number, width: number, height: number, gap: number, aspect: number): Fit {
  const rows = Math.ceil(count / cols);
  const cellWidth = (width - gap * (cols - 1)) / cols;
  const cellHeight = (height - gap * (rows - 1)) / rows;
  const tileWidth = Math.max(0, Math.min(cellWidth, cellHeight * aspect));
  return { cols, rows, tileWidth, tileHeight: tileWidth / aspect };
}

/** The column count that gives `count` tiles the largest box. Ties go to fewer columns (fewer empty cells). */
function bestFit(count: number, width: number, height: number, gap: number, aspect: number): Fit {
  let best = fitFor(count, 1, width, height, gap, aspect);
  for (let cols = 2; cols <= count; cols++) {
    const fit = fitFor(count, cols, width, height, gap, aspect);
    if (fit.tileWidth > best.tileWidth) best = fit;
  }
  return best;
}

export function galleryCapacity(input: Omit<GalleryLayoutInput, 'count'>): number {
  const {
    width,
    height,
    gap = GALLERY_TILE_GAP,
    aspect = GALLERY_TILE_ASPECT,
    minTileWidth = GALLERY_MIN_TILE_WIDTH,
    minTileWidthFraction = GALLERY_MIN_TILE_WIDTH_FRACTION,
    maxTiles = GALLERY_MAX_TILES,
  } = input;
  if (width <= 0 || height <= 0) return 1;
  const smallestTile = Math.max(minTileWidth, width * minTileWidthFraction);
  let capacity = 1;
  for (let n = 2; n <= maxTiles; n++) {
    if (bestFit(n, width, height, gap, aspect).tileWidth >= smallestTile) capacity = n;
  }
  return capacity;
}

/**
 * Lays out exactly `count` tiles; callers trim to `capacity` first (see `splitGalleryTiles`). The one
 * case that goes over is a window too small for even two tiles: your tile and "+N others" are then
 * both drawn, smaller than the minimum, rather than cut in half. Pass `capacity` when already known.
 */
export function galleryLayout(input: GalleryLayoutInput & { capacity?: number }): GalleryLayout {
  const { width, height, gap = GALLERY_TILE_GAP, aspect = GALLERY_TILE_ASPECT } = input;
  const capacity = input.capacity ?? galleryCapacity(input);
  const shown = Math.max(1, Math.floor(input.count));
  if (width <= 0 || height <= 0) {
    return { cols: 1, rows: 1, tileWidth: 0, tileHeight: 0, capacity };
  }
  const fit = bestFit(shown, width, height, gap, aspect);
  return {
    cols: fit.cols,
    rows: fit.rows,
    tileWidth: Math.floor(fit.tileWidth),
    tileHeight: Math.floor(fit.tileHeight),
    capacity,
  };
}

/** Tiles that get a seat in the grid; with more people than seats, the last seat is a "+N others" tile. */
export function galleryVisibleSlots(tileCount: number, capacity: number): number {
  const seats = Math.max(1, capacity);
  return tileCount > seats ? Math.max(1, seats - 1) : seats;
}

export function splitGalleryTiles(order: string[], capacity: number): { visible: string[]; overflow: string[] } {
  const slots = galleryVisibleSlots(order.length, capacity);
  return { visible: order.slice(0, slots), overflow: order.slice(slots) };
}
